/**
 * Ce que le module des journées pédagogiques lit et écrit.
 *
 * Les droits sont ceux de la base : un parent lit les journées et les classes,
 * range ses propres enfants dans une classe et n'inscrit que par
 * `create_order` ; l'administration programme par `jpa_enregistrer_journee`
 * et gère les classes. Rien ici ne calcule un prix ni ne décide d'une
 * éligibilité : `regles.ts` les annonce, la base les tranche.
 */
import { db } from '../payments/db';
import type { EssentielsDeLEnfant } from '../presences/essentiels';
import type { ClasseDuCentre, InscritJpa, JourneeJpa } from './regles';

const premier = <T,>(v: T | T[] | null | undefined): T | null => (Array.isArray(v) ? v[0] ?? null : v ?? null);

interface JourneeLue {
  session_id: string;
  date_journee: string;
  center_id: string;
  centre: string;
  centre_tag: string;
  school_id: string | null;
  ecole: string | null;
  prix_normal_cents: number;
  prix_reduit_cents: number;
  prix_apres_limite_cents: number;
  prix_reduit_apres_limite_cents: number;
  date_limite: string;
  ouverture: string | null;
  active: boolean;
  accueil_debut: string;
  accueil_fin: string;
  activites_debut: string;
  activites_fin: string;
  telephone_coordination: string;
  annee_scolaire: number;
  /** Absent tant que 20261026200000 n'est pas passée : `select('*')` le prend quand il vient. */
  avertissement?: string | null;
}

/**
 * Les journées, avec leurs classes admises. `depuis` (`AAAA-MM-JJ`) écarte
 * celles d'avant : le parent ne voit que celles qui viennent.
 */
export async function lireJournees(depuis?: string): Promise<JourneeJpa[]> {
  let requete = db.from('v_jpa_journees').select('*').order('date_journee').order('centre');
  if (depuis) requete = requete.gte('date_journee', depuis);
  const { data, error } = await requete;
  if (error) throw error;
  const lignes = (data ?? []) as JourneeLue[];
  if (lignes.length === 0) return [];

  const { data: admises, error: e2 } = await db
    .from('jpa_classes_admises')
    .select('session_id, classe_id')
    .in('session_id', lignes.map((l) => l.session_id));
  if (e2) throw e2;
  const parSession = new Map<string, string[]>();
  for (const a of (admises ?? []) as { session_id: string; classe_id: string }[]) {
    parSession.set(a.session_id, [...(parSession.get(a.session_id) ?? []), a.classe_id]);
  }

  return lignes.map((l) => ({
    sessionId: l.session_id,
    date: l.date_journee,
    centreId: l.center_id,
    centre: l.centre,
    centreTag: l.centre_tag,
    ecoleId: l.school_id,
    ecole: l.ecole,
    prixNormalCentimes: l.prix_normal_cents,
    prixReduitCentimes: l.prix_reduit_cents,
    prixApresLimiteCentimes: l.prix_apres_limite_cents,
    prixReduitApresLimiteCentimes: l.prix_reduit_apres_limite_cents,
    dateLimite: l.date_limite,
    ouverture: l.ouverture,
    active: l.active,
    accueilDebut: l.accueil_debut,
    accueilFin: l.accueil_fin,
    activitesDebut: l.activites_debut,
    activitesFin: l.activites_fin,
    telephoneCoordination: l.telephone_coordination,
    anneeScolaire: l.annee_scolaire,
    classesAdmises: parSession.get(l.session_id) ?? [],
    avertissement: l.avertissement,
  }));
}

interface ClasseLue {
  id: string;
  center_id: string;
  libelle: string;
  ordre: number;
  active: boolean;
}

const versClasse = (c: ClasseLue): ClasseDuCentre => ({
  id: c.id, centreId: c.center_id, libelle: c.libelle, ordre: c.ordre, active: c.active,
});

/** Les classes de centres, actives seulement sauf demande contraire. */
export async function lireClasses(centreIds: string[], toutes = false): Promise<ClasseDuCentre[]> {
  if (centreIds.length === 0) return [];
  let requete = db.from('centre_classes').select('id, center_id, libelle, ordre, active')
    .in('center_id', centreIds).order('ordre').order('libelle');
  if (!toutes) requete = requete.eq('active', true);
  const { data, error } = await requete;
  if (error) throw error;
  return ((data ?? []) as ClasseLue[]).map(versClasse);
}

/** La classe de chaque enfant pour une année scolaire, par enfant. */
export async function lireClassesDesEnfants(kidIds: string[], annee: number): Promise<Map<string, ClasseDuCentre>> {
  const resultat = new Map<string, ClasseDuCentre>();
  if (kidIds.length === 0) return resultat;
  const { data, error } = await db
    .from('kid_classes')
    .select('kid_id, classe:centre_classes(id, center_id, libelle, ordre, active)')
    .in('kid_id', kidIds)
    .eq('annee_scolaire', annee);
  if (error) throw error;
  for (const l of (data ?? []) as { kid_id: string; classe: ClasseLue | ClasseLue[] | null }[]) {
    const c = premier(l.classe);
    if (c) resultat.set(l.kid_id, versClasse(c));
  }
  return resultat;
}

/**
 * Range un enfant dans une classe pour une année scolaire. Écrit d'abord son
 * école dans sa fiche quand elle diffère : c'est la même réponse du parent
 * (« oui, mon enfant est élève de cette école »).
 */
export async function rangerEnfant(kidId: string, ecoleId: string, ecoleActuelle: string | null, annee: number, classeId: string): Promise<void> {
  if ((ecoleActuelle ?? '').trim() !== ecoleId) {
    const { error } = await db.from('kids').update({ ecole: ecoleId }).eq('id', kidId);
    if (error) throw error;
  }
  const { error } = await db
    .from('kid_classes')
    .upsert({ kid_id: kidId, annee_scolaire: annee, classe_id: classeId }, { onConflict: 'kid_id,annee_scolaire' });
  if (error) throw error;
}

/**
 * L'école sans la classe : le parent confirme que son enfant en est l'élève,
 * mais sa classe manque à la liste. Écrit l'école dans la fiche quand elle
 * diffère, et retire la classe de l'année, qui ne vaut plus — la même règle
 * que la fiche enfant quand la classe est signalée hors liste.
 */
export async function rangerSansClasse(kidId: string, ecoleId: string, ecoleActuelle: string | null, annee: number): Promise<void> {
  if ((ecoleActuelle ?? '').trim() !== ecoleId) {
    const { error } = await db.from('kids').update({ ecole: ecoleId }).eq('id', kidId);
    if (error) throw error;
  }
  const { error } = await db.from('kid_classes').delete().eq('kid_id', kidId).eq('annee_scolaire', annee);
  if (error) throw error;
}

/** Les inscriptions vivantes de la famille connectée à ces journées : session → enfants. */
export async function inscriptionsDeLaFamille(userId: string, sessionIds: string[]): Promise<Map<string, Set<string>>> {
  const resultat = new Map<string, Set<string>>();
  if (sessionIds.length === 0) return resultat;
  const { data, error } = await db
    .from('registrations')
    .select('activity_id, kid_id, payment_status, cancellation_status')
    .eq('user_id', userId)
    .in('activity_id', sessionIds);
  if (error) throw error;
  for (const r of (data ?? []) as { activity_id: string; kid_id: string; payment_status: string; cancellation_status: string | null }[]) {
    if (!['paid', 'pending'].includes(r.payment_status)) continue;
    if ((r.cancellation_status ?? 'none').startsWith('cancelled')) continue;
    const enfants = resultat.get(r.activity_id) ?? new Set<string>();
    enfants.add(r.kid_id);
    resultat.set(r.activity_id, enfants);
  }
  return resultat;
}

/* -------------------------------------------------------------------------- */
/* L'administration                                                            */
/* -------------------------------------------------------------------------- */

export interface JourneeAEnregistrer {
  sessionId: string | null;
  centreId: string;
  date: string;
  dateLimite: string;
  ouverture: string | null;
  prixNormalCentimes: number;
  prixReduitCentimes: number;
  prixApresLimiteCentimes: number;
  prixReduitApresLimiteCentimes: number;
  accueilDebut: string;
  accueilFin: string;
  activitesDebut: string;
  activitesFin: string;
  telephoneCoordination: string;
  classes: string[];
  active: boolean;
  /**
   * L'avertissement aux parents : un texte l'écrit, un texte vide le retire,
   * absent le laisse tel qu'il est. Ne l'envoyer que s'il y a quelque chose à
   * écrire ou à retirer : avant 20261026200000, la base ne connaît pas
   * `p_avertissement`, et la journée s'enregistre encore sans lui.
   */
  avertissement?: string;
}

/** Crée ou modifie une journée ; rend l'identifiant de sa session. */
export async function enregistrerJournee(j: JourneeAEnregistrer): Promise<string> {
  const { data, error } = await db.rpc('jpa_enregistrer_journee', {
    p_session_id: j.sessionId,
    p_center_id: j.centreId,
    p_date: j.date,
    p_date_limite: j.dateLimite,
    p_ouverture: j.ouverture,
    p_prix_normal_cents: j.prixNormalCentimes,
    p_prix_reduit_cents: j.prixReduitCentimes,
    p_prix_apres_limite_cents: j.prixApresLimiteCentimes,
    p_prix_reduit_apres_limite_cents: j.prixReduitApresLimiteCentimes,
    p_accueil_debut: j.accueilDebut,
    p_accueil_fin: j.accueilFin,
    p_activites_debut: j.activitesDebut,
    p_activites_fin: j.activitesFin,
    p_telephone_coordination: j.telephoneCoordination,
    p_classes: j.classes,
    p_active: j.active,
    ...(j.avertissement !== undefined ? { p_avertissement: j.avertissement } : {}),
  });
  if (error) throw error;
  return data as string;
}

interface InscritLu {
  registration_id: string;
  session_id: string;
  kid_id: string;
  prenom: string | null;
  nom: string | null;
  classe_id: string | null;
  classe: string | null;
  classe_ordre: number | null;
  parent_prenom: string | null;
  parent_nom: string | null;
  parent_telephone: string | null;
  parent_email: string | null;
  invoice_number: string | null;
  price_type: string;
  montant_cents: number;
  vivante: boolean;
}

/** Les inscrits de journées, annulés compris (l'écran choisit). */
export async function lireInscrits(sessionIds: string[]): Promise<InscritJpa[]> {
  if (sessionIds.length === 0) return [];
  const { data, error } = await db.from('v_jpa_inscriptions').select('*').in('session_id', sessionIds);
  if (error) throw error;
  return ((data ?? []) as InscritLu[]).map((i) => ({
    registrationId: i.registration_id,
    sessionId: i.session_id,
    kidId: i.kid_id,
    prenom: i.prenom ?? '',
    nom: i.nom ?? '',
    classeId: i.classe_id,
    classe: i.classe,
    classeOrdre: i.classe_ordre,
    parent: [i.parent_prenom, i.parent_nom].filter(Boolean).join(' '),
    parentTelephone: i.parent_telephone,
    parentEmail: i.parent_email,
    invoiceNumber: i.invoice_number,
    priceType: i.price_type,
    montantCentimes: i.montant_cents,
    vivante: i.vivante,
  }));
}

/** Le règlement de chaque inscription : `paid`, `partial`, `unpaid`, `cancelled`, `credited`. */
export async function lireReglements(registrationIds: string[]): Promise<Map<string, string>> {
  const resultat = new Map<string, string>();
  for (let i = 0; i < registrationIds.length; i += 1000) {
    const { data, error } = await db.rpc('reglement_des_inscriptions', {
      p_inscriptions: registrationIds.slice(i, i + 1000),
    });
    if (error) throw error;
    for (const r of (data ?? []) as { registration_id: string; settlement: string }[]) {
      resultat.set(r.registration_id, r.settlement);
    }
  }
  return resultat;
}

/*
  Le rappel de paiement (`20261019190000`). Tant que la migration n'est pas
  appliquée, l'API ne connaît ni la fonction (`PGRST202`) ni la vue
  (`PGRST205`) : l'écran le dit au lieu de planter.
*/
const pasInstalle = (e: { code?: string } | null) => e?.code === 'PGRST202' || e?.code === 'PGRST205';

export interface EtatDesRappels {
  /** Le dernier jour couvert par un relevé bancaire importé (AAAA-MM-JJ). */
  couvertureBancaire: string | null;
  /** Le nombre de factures échues et non réglées, par état (`a_relancer`, `attend_le_releve`…). */
  etats: Record<string, number>;
  envoyes: number;
  echecs: number;
  dernierEnvoi: string | null;
}

/** `null` : le rappel n'est pas installé sur cette base. */
export async function lireEtatDesRappels(): Promise<EtatDesRappels | null> {
  const { data, error } = await db.rpc('etat_rappels_jpa');
  if (pasInstalle(error)) return null;
  if (error) throw error;
  const e = (data ?? {}) as {
    couverture_bancaire?: string | null; etats?: Record<string, number>;
    envoyes?: number; echecs?: number; dernier_envoi?: string | null;
  };
  return {
    couvertureBancaire: e.couverture_bancaire ?? null,
    etats: e.etats ?? {},
    envoyes: Number(e.envoyes ?? 0),
    echecs: Number(e.echecs ?? 0),
    dernierEnvoi: e.dernier_envoi ?? null,
  };
}

export interface RappelDeFacture {
  statut: 'en_cours' | 'a_reessayer' | 'envoye' | 'echec';
  envoyeLe: string | null;
  derniereErreur: string | null;
}

/** Le rappel de chaque facture qui en a un, par numéro de facture. */
export async function lireRappels(numeros: string[]): Promise<Map<string, RappelDeFacture>> {
  const resultat = new Map<string, RappelDeFacture>();
  for (let i = 0; i < numeros.length; i += 200) {
    const { data, error } = await db
      .from('v_rappels_paiement_jpa')
      .select('invoice_number, statut, envoye_le, derniere_erreur')
      .in('invoice_number', numeros.slice(i, i + 200));
    if (pasInstalle(error)) return resultat;
    if (error) throw error;
    for (const r of (data ?? []) as { invoice_number: string; statut: RappelDeFacture['statut']; envoye_le: string | null; derniere_erreur: string | null }[]) {
      resultat.set(r.invoice_number, { statut: r.statut, envoyeLe: r.envoye_le, derniereErreur: r.derniere_erreur });
    }
  }
  return resultat;
}

/*
  Ce que les listes d'une journée disent de chaque enfant : les « essentiels »
  des listes en ligne des stages (`presences_lien_essentiels`), lus ici par
  l'équipe, colonne pour colonne — allergies, régime, médicaments, à savoir,
  départ (seul, heure, personnes autorisées), espace des familles. Les mêmes
  règles décident ensuite de ce qui se montre (`presences/essentiels.ts`) :
  la feuille et l'écran du téléphone disent la même chose.
*/

const PAR_REQUETE = 100;

async function parPaquets<T>(ids: string[], lire: (paquet: string[]) => PromiseLike<{ data: unknown; error: unknown }>): Promise<T[]> {
  const lignes: T[] = [];
  for (let i = 0; i < ids.length; i += PAR_REQUETE) {
    const { data, error } = await lire(ids.slice(i, i + PAR_REQUETE));
    if (error) throw error;
    lignes.push(...((data ?? []) as T[]));
  }
  return lignes;
}

interface AllergiesLues { kid_id: string; has_allergies: boolean | null; allergies_details: string | null; allergies_consequences: string | null; special_diet: boolean | null; diet_details: string | null }
interface SanteLue { kid_id: string; medication: boolean | null; medication_details: string | null; medication_autonomy: boolean | null; specific_medical: string | null }
interface DepartLu { kid_id: string; leaves_alone: boolean | null; departure_time: string | null; pickup_people_ids: string[] | null }
interface DroitLu { kid_id: string; internal_spaces: boolean | null }
interface PersonneLue { id: string; first_name: string | null; last_name: string | null }

/** Les essentiels de chaque inscrit, par inscription. */
export async function lireEssentiels(inscrits: Pick<InscritJpa, 'registrationId' | 'kidId'>[]): Promise<Map<string, EssentielsDeLEnfant>> {
  const resultat = new Map<string, EssentielsDeLEnfant>();
  const kidIds = [...new Set(inscrits.map((i) => i.kidId))];
  if (kidIds.length === 0) return resultat;
  const [allergies, sante, departs, droits] = await Promise.all([
    parPaquets<AllergiesLues>(kidIds, (p) => db.from('kid_allergies')
      .select('kid_id, has_allergies, allergies_details, allergies_consequences, special_diet, diet_details').in('kid_id', p)),
    parPaquets<SanteLue>(kidIds, (p) => db.from('kid_health')
      .select('kid_id, medication, medication_details, medication_autonomy, specific_medical').in('kid_id', p)),
    parPaquets<DepartLu>(kidIds, (p) => db.from('kid_departure')
      .select('kid_id, leaves_alone, departure_time, pickup_people_ids').in('kid_id', p)),
    parPaquets<DroitLu>(kidIds, (p) => db.from('kid_image_rights').select('kid_id, internal_spaces').in('kid_id', p)),
  ]);
  const personnesIds = [...new Set(departs.flatMap((d) => d.pickup_people_ids ?? []))];
  const personnes = new Map(
    (await parPaquets<PersonneLue>(personnesIds, (p) => db.from('authorized_persons').select('id, first_name, last_name').in('id', p)))
      .map((p) => [p.id, { prenom: p.first_name?.trim() ?? '', nom: p.last_name?.trim() ?? '' }]),
  );
  const parKid = <T extends { kid_id: string }>(lignes: T[]) => new Map(lignes.map((l) => [l.kid_id, l]));
  const a = parKid(allergies);
  const h = parKid(sante);
  const d = parKid(departs);
  const ir = parKid(droits);

  for (const i of inscrits) {
    const al = a.get(i.kidId);
    const sa = h.get(i.kidId);
    const de = d.get(i.kidId);
    resultat.set(i.registrationId, {
      registrationId: i.registrationId,
      photo: null,
      imageOk: ir.get(i.kidId)?.internal_spaces === true,
      sante: {
        allergies: al?.has_allergies ?? null,
        allergiesDetails: al?.allergies_details ?? null,
        allergiesConsequences: al?.allergies_consequences ?? null,
        regime: al?.special_diet ?? null,
        regimeDetails: al?.diet_details ?? null,
        medicaments: sa?.medication ?? null,
        medicamentsDetails: sa?.medication_details ?? null,
        medicamentsAutonomie: sa?.medication_autonomy ?? null,
        aSavoir: sa?.specific_medical ?? null,
      },
      depart: {
        partSeul: de?.leaves_alone ?? null,
        heure: de?.departure_time?.trim() || null,
        personnes: (de?.pickup_people_ids ?? [])
          .map((id) => personnes.get(id))
          .filter((p): p is { prenom: string; nom: string } => Boolean(p && (p.prenom || p.nom)))
          .sort((x, y) => x.nom.localeCompare(y.nom, 'fr') || x.prenom.localeCompare(y.prenom, 'fr')),
      },
    });
  }
  return resultat;
}

/* -------------------------------------------------------------------------- */
/* Les groupes d'une journée                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Le groupe de chaque inscription de la journée (`registrations.subgroup_id`,
 * les sous-groupes des stages) : inscription → groupe, ou `null`.
 */
export async function lireAffectations(sessionId: string): Promise<Map<string, string | null>> {
  const { data, error } = await db.from('registrations').select('id, subgroup_id').eq('activity_id', sessionId);
  if (error) throw error;
  return new Map(((data ?? []) as { id: string; subgroup_id: string | null }[]).map((r) => [r.id, r.subgroup_id]));
}

/* -------------------------------------------------------------------------- */
/* Le centre, son école, ses classes                                           */
/* -------------------------------------------------------------------------- */

export async function lierEcole(centreId: string, ecoleId: string | null, centreDeVacances: boolean): Promise<void> {
  const { error } = await db.from('centers')
    .update({ school_id: ecoleId, centre_de_vacances: centreDeVacances })
    .eq('id', centreId);
  if (error) throw error;
}

export async function ajouterClasse(centreId: string, libelle: string, ordre: number): Promise<void> {
  const { error } = await db.from('centre_classes').insert({ center_id: centreId, libelle: libelle.trim(), ordre });
  if (error) throw error;
}

export async function modifierClasse(id: string, champs: { libelle?: string; ordre?: number; active?: boolean }): Promise<void> {
  const { error } = await db.from('centre_classes')
    .update({ ...champs, ...(champs.libelle !== undefined ? { libelle: champs.libelle.trim() } : {}) })
    .eq('id', id);
  if (error) throw error;
}

/** Combien d'enfants sont rangés dans chaque classe, toutes années confondues. */
export async function enfantsParClasse(classeIds: string[]): Promise<Map<string, number>> {
  const resultat = new Map<string, number>();
  if (classeIds.length === 0) return resultat;
  const { data, error } = await db.from('kid_classes').select('classe_id').in('classe_id', classeIds);
  if (error) throw error;
  for (const l of (data ?? []) as { classe_id: string }[]) {
    resultat.set(l.classe_id, (resultat.get(l.classe_id) ?? 0) + 1);
  }
  return resultat;
}

/* -------------------------------------------------------------------------- */
/* Les conditions générales                                                    */
/* -------------------------------------------------------------------------- */

const CLE_CONDITIONS = 'jpa_conditions_generales';

/** Le lien vers les conditions générales des JPA, ou `null` s'il n'est pas encore donné. */
export async function lienDesConditions(): Promise<string | null> {
  const { data, error } = await db.from('site_reglages').select('valeur').eq('cle', CLE_CONDITIONS).maybeSingle();
  if (error) throw error;
  const url = ((data as { valeur?: { url?: string } } | null)?.valeur?.url ?? '').trim();
  return url || null;
}

export async function changerLienDesConditions(url: string): Promise<void> {
  const { error } = await db.from('site_reglages').upsert(
    { cle: CLE_CONDITIONS, valeur: { url: url.trim() }, maj_le: new Date().toISOString() },
    { onConflict: 'cle' },
  );
  if (error) throw error;
}
